import React, { useContext, useEffect, useState, useRef } from "react";
import { MyContext, ListContext } from "./MyContext";
import {
  dateISOtoStringShort,
  dateISOtoStringShortWithYear,
  dateISOtoString,
  dateISOtoStringMed,
  dateISOtoStringShortUTC,
} from "./momentUtils";
import { TableHeader2 } from "./formComponents";
import SelectAccountHolder from "./selectAccountHolder";
import AlertModal, { useAlertModal } from "./AlertModal";
import { SelectTemplateFilter, Active } from "./searchFilterReports";
import SearchFilterDate from "./searchFilterDates";
import { FeatureNotice } from "./featureAccess";

import COLORS from "./styleConstants";
import Auth from "./Auth";

export const AdminSubscriptionReport = () => {
  const { mystate, dispatch } = useContext(MyContext);
  const { listState, dispatch2 } = useContext(ListContext);
  const billingPersonIds = listState.filter?.billing_person_id;
  const productIds = listState.filter?.product;
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [cancelingId, setCancelingId] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [openCancelMenuId, setOpenCancelMenuId] = useState(null);
  const { modalConfig, showSuccess } = useAlertModal();
  const cancelingRef = useRef(false);

  useEffect(() => {
    if (openCancelMenuId === null) return;

    const closeMenu = (event) => {
      if (!event.target.closest("[data-subscription-menu]")) {
        setOpenCancelMenuId(null);
      }
    };
    const closeWithEscape = (event) => {
      if (event.key === "Escape") setOpenCancelMenuId(null);
    };

    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [openCancelMenuId]);

  const cancelSubscription = async (subscription) => {
    if (cancelingRef.current || !window.confirm(`Cancel subscription?`)) return;

    cancelingRef.current = true;
    setCancelingId(subscription.id);
    setCancelError("");
    try {
      const result = await Auth.api(
        "subscriptions/cancelClientSubscription",
        "post",
        { subscription_id: subscription.id },
      );
      if (
        result?.data?.status !== "canceled" ||
        String(result.data.subscription_id) !== String(subscription.id) ||
        ![0, false].includes(result.data.is_active)
      ) {
        throw new Error("Subscription cancellation was not confirmed.");
      }
      setSubscriptions((current) =>
        current.filter((item) => item.id !== subscription.id),
      );
      showSuccess(`Subscription #${subscription.id} was canceled.`, "Canceled");
    } catch (error) {
      setCancelError(
        error.response?.data?.message ||
          error.message ||
          "Unable to cancel this subscription.",
      );
    } finally {
      cancelingRef.current = false;
      setCancelingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setLoadError("");
      dispatch({ type: "FETCH_INIT" });

      try {
        const params = {
          billing_person_id: billingPersonIds,
          product: productIds,
        };

        const result = await Auth.api(
          "subscriptions/getFarmSubscriptions",
          "get",
          params,
        );

        if (cancelled) return;
        const items = result?.data?.subscriptions;
        if (!Array.isArray(items)) {
          throw new Error("Unable to confirm the subscription report results.");
        }
        setSubscriptions(items);
        dispatch({ type: "FETCH_SUCCESS" });
      } catch (error) {
        if (cancelled) return;
        setLoadError(
          error.response?.data?.message ||
            error.message ||
            "Unable to load recurring charges.",
        );
        dispatch({ type: "FETCH_ERROR" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [billingPersonIds, productIds, dispatch]);

  const totalNextCharge = subscriptions.reduce(
    (sum, sub) => sum + Number(sub.next_invoice_total || 0),
    0,
  );

  return (
    <div style={{ width: "100%", padding: 20 }}>
      <AlertModal {...modalConfig} />
      <h4
        style={{
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        Active Recurring Charges
      </h4>
      <div style={{ display: "flex" }}>
        <SelectAccountHolder />
      </div>
      <SelectTemplateFilter
        options={mystate.options_product}
        isMulti
        label={"product"}
        list={listState.filter_labels.product || null}
        value={listState.filter.product || null}
        maxWidth={200}
      />

      {cancelError && (
        <div role="alert" style={{ color: "#b42318", marginBottom: 12 }}>
          {cancelError}
        </div>
      )}
      {loading ? (
        <div>Loading subscriptions...</div>
      ) : loadError ? (
        <div role="alert" style={{ color: "#b42318", marginBottom: 12 }}>
          {loadError}
        </div>
      ) : subscriptions.length === 0 ? (
        <div>No recurring charges found.</div>
      ) : (
        <>
          <TableHeader2 title="Subscription Report" />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginBottom: 20,
            }}
          >
            <div
              className="headerrow"
              style={{
                display: "flex",
                flexWrap: "wrap",
                color: COLORS.blue,
                fontWeight: "bold",
                padding: "3px 7px",
                textAlign: "left",
              }}
            >
              <div style={{ width: "20%" }}>account</div>
              <div style={{ width: "20%" }}>student</div>

              <div style={{ width: "20%" }}>product</div>
              <div style={{ width: "20%" }}>next charge date</div>
              <div style={{ width: "15%", textAlign: "right" }}>amount</div>
            </div>
            <div className="purchaseListBody">
              {subscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="rowMain"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "3px 7px",
                    fontSize: 14,
                    borderBottom: "solid 1pt lightgrey",
                    marginBottom: 0,
                    marginTop: 25,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      fontWeight: "bold",
                      marginTop: 0,
                    }}
                  >
                    <div>
                      Next charge:{" "}
                      {new Intl.DateTimeFormat("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        timeZone: "UTC",
                      }).format(new Date(sub.current_period_start))}
                      {" \u00b7 $"}
                      {Number(sub.next_invoice_total || 0).toFixed(2)}
                    </div>
                    <div
                      style={{
                        marginLeft: "auto",
                        textAlign: "right",
                        color: COLORS.textgrey,
                        fontWeight: "normal",
                      }}
                    >
                      Created:{" "}
                      {sub.created_at
                        ? dateISOtoStringShortWithYear(sub.created_at)
                        : "-"}
                    </div>
                    <div
                      data-subscription-menu
                      style={{ position: "relative", flexShrink: 0 }}
                    >
                      <button
                        type="button"
                        disabled={cancelingId !== null}
                        aria-label={`Actions for subscription #${sub.id}`}
                        aria-haspopup="menu"
                        aria-expanded={openCancelMenuId === sub.id}
                        onClick={() =>
                          setOpenCancelMenuId((current) =>
                            current === sub.id ? null : sub.id,
                          )
                        }
                        style={{
                          background: "transparent",
                          border: "none",
                          color: COLORS.orange,
                          cursor: cancelingId !== null ? "default" : "pointer",
                          fontSize: 20,
                          lineHeight: 1,
                          padding: "2px 6px",
                        }}
                      >
                        ...
                      </button>
                      {openCancelMenuId === sub.id && (
                        <div
                          role="menu"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "100%",
                            zIndex: 10,
                            minWidth: 170,
                            padding: 4,
                            border: "1px solid #e2e5e9",
                            borderRadius: 4,
                            background: "white",
                            boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
                          }}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            disabled={cancelingId !== null}
                            onClick={() => {
                              setOpenCancelMenuId(null);
                              cancelSubscription(sub);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: "7px 10px",
                              border: "none",
                              background: "transparent",
                              color: "#b42318",
                              textAlign: "left",
                              cursor:
                                cancelingId !== null ? "default" : "pointer",
                              fontSize: 13,
                            }}
                          >
                            {cancelingId === sub.id
                              ? "Canceling..."
                              : "Cancel subscription"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {sub.subscription_items?.length > 0 ? (
                    sub.subscription_items.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          display: "flex",
                          flex: "1 1 100%",
                          flexWrap: "wrap",
                          padding: "1px 0",
                        }}
                      >
                        <div style={{ width: "20%" }}>
                          {sub.billing_person_name || ""}
                        </div>
                        <div style={{ width: "20%" }}>
                          {item.student_name || ""}
                        </div>
                        <div style={{ width: "20%" }}>
                          {item.product_name || ""}
                        </div>
                        <div style={{ width: "20%" }}>
                          {dateISOtoStringShortUTC(sub.current_period_start)}
                        </div>
                        <div style={{ width: "15%", textAlign: "right" }}>
                          ${Number(item.amount || 0).toFixed(2)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ fontStyle: "italic", padding: "1px 0" }}>
                      No items
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
